import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useFetchKYC, useInitAccountKYC, useInitAccountParticipantKYC } from '@/kyc/kycQueries'
import { KYC_INIT_TARGET, resolveKycInitTarget } from '@/kyc/kycFlowResolver'
import { KycProviders } from '@/kyc/types'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'

export function useFrankieOneInitKyc() {
    const initAccountKyc = useInitAccountKYC()
    const initAccountParticipantKyc = useInitAccountParticipantKYC()
    const location = useLocation()
    const { user } = useUserStore()
    const account = useCurrentAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const { data: accountKyc } = useFetchKYC(activeAccountId, { enabled: true })
    const [isKYCStart, setIsKYCStart] = useState(false)
    const recoveredParticipantInitRef = useRef(false)

    const participantIdOverride = (location.state as { participantId?: string } | null)?.participantId?.trim() || null

    const resolvedTarget = resolveKycInitTarget({
        pathname: location.pathname,
        kyc: accountKyc,
        userId: user?.userId,
    })

    const backendRoutedTarget = initAccountKyc.data?.routing?.target
    const backendRoutedParticipantId = initAccountKyc.data?.referenceId
    const target = participantIdOverride
        ? KYC_INIT_TARGET.ACCOUNT_PARTICIPANT
        : backendRoutedTarget === 'PARTICIPANT'
          ? KYC_INIT_TARGET.ACCOUNT_PARTICIPANT
          : resolvedTarget.target
    const participantId = participantIdOverride ?? backendRoutedParticipantId ?? resolvedTarget.participantId
    const shouldInitParticipant = target === KYC_INIT_TARGET.ACCOUNT_PARTICIPANT && !!participantId

    const hasParticipantInitState =
        !!initAccountParticipantKyc.data ||
        initAccountParticipantKyc.isPending ||
        initAccountParticipantKyc.isSuccess ||
        initAccountParticipantKyc.isError

    const isAccountInitRoutedToParticipant = backendRoutedTarget === 'PARTICIPANT'

    const activeInitKyc =
        hasParticipantInitState || isAccountInitRoutedToParticipant ? initAccountParticipantKyc : initAccountKyc

    const triggerInitKyc = useCallback(
        (participantIdToInit?: string) => {
            if (!activeAccountId) {
                return
            }

            if (participantIdToInit) {
                initAccountParticipantKyc.mutate({
                    accountId: activeAccountId,
                    participantId: participantIdToInit,
                    provider: KycProviders.FRANKIEONE,
                    accountType: account?.accountType,
                })
                return
            }

            initAccountKyc.mutate({
                accountId: activeAccountId,
                provider: KycProviders.FRANKIEONE,
                accountType: account?.accountType,
            })
        },
        [account?.accountType, activeAccountId, initAccountKyc, initAccountParticipantKyc],
    )

    useEffect(() => {
        if (!isKYCStart) return
        if (backendRoutedTarget !== 'PARTICIPANT') return
        if (!participantId || recoveredParticipantInitRef.current) return
        recoveredParticipantInitRef.current = true
        triggerInitKyc(participantId)
    }, [backendRoutedTarget, isKYCStart, participantId, triggerInitKyc])

    const startInitKyc = useCallback(() => {
        setIsKYCStart(true)
        recoveredParticipantInitRef.current = false
        triggerInitKyc(shouldInitParticipant ? participantId : undefined)
    }, [participantId, shouldInitParticipant, triggerInitKyc])

    return {
        pathname: location.pathname,
        activeAccountId,
        isKYCStart,
        startInitKyc,
        target,
        kyc: activeInitKyc.data,
        isPending: activeInitKyc.isPending,
        isSuccess: activeInitKyc.isSuccess,
        isError: activeInitKyc.isError,
        error: activeInitKyc.error,
    }
}
